import { Fragment, useEffect, useState } from "react";
import { Map, Source, Layer } from "react-map-gl/mapbox";
import { API_URL, MAPBOX_TOKEN } from "../../config";
import { authFetch } from "../../utils";

function formatDate(ts) {
  return new Date(ts).toLocaleString("es-ES");
}

function polygonBounds(geometry) {
  const coords = geometry?.coordinates?.[0] ?? [];
  if (!coords.length) return null;

  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  coords.forEach(([lng, lat]) => {
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  });

  return { minLng, minLat, maxLng, maxLat };
}

// Mapa de solo lectura, completamente separado del mapa donde se dibujan
// alertas nuevas (sin controles, sin MapboxDraw, color distinto), para que
// nunca se confunda con una alerta en curso.
function PolygonPreviewMap({ polygon }) {
  const bounds = polygonBounds(polygon);

  return (
    <div style={{ position: "relative", height: 220, borderRadius: 6, overflow: "hidden", border: "1px solid #ddd", marginTop: 8 }}>
      <div
        style={{
          position: "absolute",
          top: 6,
          left: 6,
          zIndex: 1,
          background: "rgba(0,0,0,0.65)",
          color: "#fff",
          fontSize: 11,
          fontWeight: "bold",
          padding: "3px 8px",
          borderRadius: 4,
        }}
      >
        Histórico — solo lectura
      </div>
      <Map
        initialViewState={
          bounds
            ? {
                bounds: [bounds.minLng, bounds.minLat, bounds.maxLng, bounds.maxLat],
                fitBoundsOptions: { padding: 24 },
              }
            : { longitude: -0.50267, latitude: 39.53351, zoom: 12 }
        }
        style={{ width: "100%", height: "100%" }}
        mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
        mapboxAccessToken={MAPBOX_TOKEN}
        dragRotate={false}
        touchPitch={false}
      >
        <Source type="geojson" data={{ type: "Feature", geometry: polygon, properties: {} }}>
          <Layer
            id="history-polygon-fill"
            type="fill"
            paint={{ "fill-color": "#2563eb", "fill-opacity": 0.25 }}
          />
          <Layer
            id="history-polygon-outline"
            type="line"
            paint={{ "line-color": "#2563eb", "line-width": 2 }}
          />
        </Source>
      </Map>
    </div>
  );
}

const PERIODS = [
  { label: "Último mes", days: 30 },
  { label: "Últimos 3 meses", days: 90 },
  { label: "Últimos 6 meses", days: 180 },
  { label: "Último año", days: 365 },
];

export default function AlerpatHistoryModal({ onClose, restricted = false }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPolygonId, setExpandedPolygonId] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setLoading(true);
    authFetch(`${API_URL}alerpat/alerts?days=${days}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Error ${res.status}`);
        return res.json();
      })
      .then(setAlerts)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "90%",
          maxWidth: 760,
          maxHeight: "85vh",
          overflowY: "auto",
          backgroundColor: "#fff",
          borderRadius: 8,
          padding: "1.5rem",
          boxShadow: "0 2px 12px rgba(0,0,0,0.3)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", gap: 12 }}>
          <h3 style={{ margin: 0 }}>Histórico de avisos AlerPAT</h3>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              style={{ fontSize: 12, padding: "4px 6px", borderRadius: 4, border: "1px solid #ccc" }}
            >
              {PERIODS.map((p) => (
                <option key={p.days} value={p.days}>{p.label}</option>
              ))}
            </select>
            <button onClick={onClose} style={{ border: "none", background: "none", fontSize: 18, cursor: "pointer" }}>✕</button>
          </div>
        </div>

        {loading && <p style={{ color: "#6c757d" }}>Cargando...</p>}
        {error && <p style={{ color: "#c00" }}>Error: {error}</p>}

        {!loading && !error && alerts.length === 0 && (
          <p style={{ color: "#6c757d" }}>Todavía no se ha enviado ningún aviso.</p>
        )}

        {!loading && !error && alerts.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {(restricted
                    ? ["Fecha", "Título / texto", "Envíos exitosos"]
                    : ["Fecha", "Tipo", "Título / texto", "Registrados", "En zona", "Polígono"]
                  ).map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 10px", borderBottom: "2px solid #dee2e6", color: "#495057" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {alerts.map((a) => (
                  <Fragment key={a.alertId}>
                    <tr>
                      <td style={cellStyle}>{formatDate(a.createdAt)}</td>
                      {!restricted && (
                        <td style={cellStyle}>
                          <span
                            style={{
                              padding: "2px 8px",
                              borderRadius: 10,
                              fontSize: 11,
                              fontWeight: "bold",
                              color: "#fff",
                              backgroundColor: a.type === "emergencia" ? "#c53030" : "#b45309",
                            }}
                          >
                            {a.type}
                          </span>
                        </td>
                      )}
                      <td style={cellStyle}>
                        <div style={{ fontWeight: "bold" }}>{a.titulo}</div>
                        <div style={{ color: "#666", fontSize: 12 }}>{a.texto}</div>
                      </td>
                      <td style={cellStyle}>{a.fcmSent}{!restricted && a.fcmFailed > 0 ? ` (${a.fcmFailed} fallidos)` : ""}</td>
                      {!restricted && <td style={cellStyle}>{a.recipientCount}</td>}
                      {!restricted && (
                        <td style={cellStyle}>
                          {a.polygon ? (
                            <button
                              onClick={() => setExpandedPolygonId(expandedPolygonId === a.alertId ? null : a.alertId)}
                              style={{ border: "1px solid #ccc", borderRadius: 4, background: "#fff", cursor: "pointer", fontSize: 11, padding: "2px 6px" }}
                            >
                              {expandedPolygonId === a.alertId ? "Ocultar" : "Ver"}
                            </button>
                          ) : "—"}
                        </td>
                      )}
                    </tr>
                    {!restricted && expandedPolygonId === a.alertId && (
                      <tr>
                        <td colSpan={6} style={{ padding: "0 10px 10px", borderBottom: "1px solid #f0f0f0" }}>
                          <PolygonPreviewMap polygon={a.polygon} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const cellStyle = { padding: "8px 10px", borderBottom: "1px solid #f0f0f0", verticalAlign: "top" };
